import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatRM } from "@/lib/api";
import { ExternalLink } from "lucide-react";

const STATUSES = ["", "pending_payment", "proof_uploaded", "confirmed", "rejected", "cancelled"];

export default function AdminOrders() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/orders", { params: status ? { status } : {} })
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [status]);

  return (
    <div className="p-6 md:p-10" data-testid="admin-orders-page">
      <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">
        {t("admin.orderList")}
      </h1>

      <div className="mt-6 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors duration-200 ${
              status === s
                ? "bg-[#005870] text-white border-[#005870]"
                : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e]"
            }`}
            data-testid={`filter-${s || "all"}`}
          >
            {s ? t(`order.statuses.${s}`) : t("common.all")}
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto">
        {loading ? (
          <div className="text-[#4a6e77]">{t("common.loading")}</div>
        ) : orders.length === 0 ? (
          <div className="rounded-md border border-[#e5ebeb] bg-white p-10 text-[#4a6e77] text-center">
            {t("common.empty")}
          </div>
        ) : (
          <table className="w-full border-collapse text-sm" data-testid="admin-orders-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#4a6e77]">
                <th className="py-3 px-4 border-b border-[#e5ebeb]">Order</th>
                <th className="py-3 px-4 border-b border-[#e5ebeb]">Customer</th>
                <th className="py-3 px-4 border-b border-[#e5ebeb]">Total</th>
                <th className="py-3 px-4 border-b border-[#e5ebeb]">Status</th>
                <th className="py-3 px-4 border-b border-[#e5ebeb]">Date</th>
                <th className="py-3 px-4 border-b border-[#e5ebeb]"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-[#fdfbf7] transition-colors duration-150" data-testid={`admin-order-${o.id}`}>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] font-medium text-[#003645]">#{o.order_no}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">
                    <div className="text-[#003645]">{o.user_name}</div>
                    <div className="text-xs">{o.user_email}</div>
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#005870] font-semibold">{formatRM(o.total)}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb]">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        o.status === "confirmed"
                          ? "bg-[#00868e]/10 text-[#00868e]"
                          : o.status === "rejected" || o.status === "cancelled"
                            ? "bg-red-50 text-red-700"
                            : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {t(`order.statuses.${o.status}`)}
                    </span>
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77] text-xs">
                    {new Date(o.created_at).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb]">
                    <Link
                      to={`/admin/orders/${o.id}`}
                      className="inline-flex items-center gap-1 text-[#005870] hover:text-[#00868e] font-medium"
                      data-testid={`view-admin-order-${o.id}`}
                    >
                      {t("common.view")} <ExternalLink size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
